import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ServiceAvailabilityStore } from './service-availability.store';

/**
 * Covers the whole app while the platform cannot be used.
 *
 * Says only what we actually know, and there are three different amounts of knowing: the backend
 * told us it is closed; the browser knows it has no network; or neither, in which case the cause
 * is genuinely unknown and the copy blames nobody. Told to "check your connection" during an
 * outage of ours, a venue spends the morning on the phone to their ISP.
 *
 * There is no way past it, by design. Every screen behind it needs the backend, so a dismissable
 * version would only trade one honest screen for a tour of broken ones — and worse, for forms
 * that look like they saved.
 */
@Component({
  selector: 'app-service-unavailable',
  standalone: true,
  imports: [TranslateModule, DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (store.isBlocked()) {
      <div class="su-overlay" role="alertdialog" aria-modal="true" aria-labelledby="su-title">
        <div class="su-card">
          <div class="su-icon" [class.su-icon--maintenance]="isMaintenance()" aria-hidden="true">
            @if (isMaintenance()) {
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                   stroke-linecap="round" stroke-linejoin="round">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76Z" />
              </svg>
            } @else {
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
                   stroke-linecap="round" stroke-linejoin="round">
                <path d="M1 1l22 22" />
                <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
                <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
                <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
                <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
                <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                <path d="M12 20h.01" />
              </svg>
            }
          </div>

          <h1 id="su-title" class="su-title">{{ titleKey() | translate }}</h1>

          <p class="su-body">{{ bodyKey() | translate }}</p>

          <!-- Only when there is one. An absent estimate leaves the card saying nothing about
               timing, which is better than inventing a reassurance. -->
          @if (isMaintenance() && endsAt(); as when) {
            <p class="su-eta">
              {{ 'SERVICE_STATUS.EXPECTED_BACK' | translate: { time: when | date: 'shortTime' } }}
            </p>
          }

          <button type="button" class="su-retry" [disabled]="retrying()" (click)="retry()">
            {{ (retrying() ? 'SERVICE_STATUS.CHECKING' : 'SERVICE_STATUS.RETRY') | translate }}
          </button>

          <p class="su-hint">{{ 'SERVICE_STATUS.AUTO_RETRY' | translate }}</p>
        </div>
      </div>
    }
  `,
  styles: [`
    /* Above the toast layer (2000): a toast about a failed request must not sit on top of the
       screen explaining why every request is failing. */
    .su-overlay {
      position: fixed;
      inset: 0;
      z-index: 3000;
      display: grid;
      place-items: center;
      padding: 24px;
      background: var(--bg-color, #f9fafb);
    }

    .su-card {
      width: 100%;
      max-width: 26rem;
      padding: 32px;
      text-align: center;
      background: var(--surface-color, #fff);
      border: 1px solid var(--border-color, #e5e7eb);
      border-radius: calc(var(--border-radius, 8px) * 1.5);
      box-shadow: var(--shadow-lg, 0 10px 15px -3px rgba(0, 0, 0, 0.1));
    }

    .su-icon {
      width: 56px;
      height: 56px;
      margin: 0 auto 20px;
      display: grid;
      place-items: center;
      border-radius: 50%;
      color: var(--color-error, #dc2626);
      background: color-mix(in srgb, var(--color-error, #dc2626) 12%, transparent);

      svg { width: 28px; height: 28px; }
    }

    /* Planned work is not a fault, and colouring it like one makes a two-minute window read as an
       outage. */
    .su-icon--maintenance {
      color: var(--color-warning, #d97706);
      background: color-mix(in srgb, var(--color-warning, #d97706) 12%, transparent);
    }

    .su-title {
      margin: 0 0 10px;
      font-size: 1.25rem;
      font-weight: 600;
      color: var(--text-color, #1a1a2e);
    }

    .su-body {
      margin: 0;
      font-size: 0.9375rem;
      line-height: 1.55;
      color: var(--text-muted, #6b7280);
    }

    .su-eta {
      margin: 12px 0 0;
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--color-warning, #d97706);
    }

    .su-retry {
      width: 100%;
      margin-top: 24px;
      padding: 11px 20px;
      font: inherit;
      font-weight: 600;
      color: var(--text-on-primary, #fff);
      background: var(--color-primary, #4f46e5);
      border: none;
      border-radius: var(--border-radius, 8px);
      cursor: pointer;
      transition: background 0.15s ease;

      &:hover:not(:disabled) { background: var(--color-primary-hover, #4338ca); }
      &:disabled { opacity: 0.6; cursor: default; }
    }

    .su-hint {
      margin: 12px 0 0;
      font-size: 0.8125rem;
      color: var(--text-light, #9ca3af);
    }
  `]
})
export class ServiceUnavailableComponent {
  readonly store = inject(ServiceAvailabilityStore);

  readonly retrying = signal(false);

  readonly kind = computed(() => this.store.availability().kind);
  readonly isMaintenance = computed(() => this.kind() === 'maintenance');
  readonly isOffline = computed(() => this.kind() === 'offline');

  readonly titleKey = computed(() => {
    switch (this.kind()) {
      case 'maintenance': return 'SERVICE_STATUS.MAINTENANCE_TITLE';
      case 'offline':     return 'SERVICE_STATUS.OFFLINE_TITLE';
      default:            return 'SERVICE_STATUS.UNREACHABLE_TITLE';
    }
  });

  readonly bodyKey = computed(() => {
    switch (this.kind()) {
      case 'maintenance': return 'SERVICE_STATUS.MAINTENANCE_BODY';
      case 'offline':     return 'SERVICE_STATUS.OFFLINE_BODY';
      default:            return 'SERVICE_STATUS.UNREACHABLE_BODY';
    }
  });

  readonly endsAt = computed(() => this.store.availability().endsAt);

  /**
   * The button is a courtesy, not the mechanism: the store polls on its own the whole time this
   * screen is up, so a user who does nothing still gets back in. It exists because waiting in
   * front of a screen with no controls feels like being stuck even when it is working.
   */
  async retry(): Promise<void> {
    if (this.retrying()) return;
    this.retrying.set(true);

    try {
      await this.store.recheck();
    } finally {
      this.retrying.set(false);
    }
  }
}
